import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(ArcElement, CategoryScale, LinearScale, Tooltip, Legend);

const InventoryStatusCard = () => {
  const [inventoryData, setInventoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await axios.get('https://api.spherewms.com/v2/wms/wisdom-s/inventory', {
          headers: {
            Authorization: 'Bearer edfcdfb396fca8cd94fba87c8c3592a1',
          },
        });
        setInventoryData(formatResponseData(response.data));
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to fetch inventory:", error);
        setError("Failed to fetch inventory data.");
        setIsLoading(false);
      }
    };

    fetchInventory();
  }, []);

  const formatResponseData = (data) => {
    let formattedData = [];
    Object.entries(data).forEach(([clientCode, products]) => {
      Object.values(products).forEach((product) => {
        formattedData.push({ clientCode, ...product });
      });
    });
    return formattedData;
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  // Aggregate inventory data
  const totals = inventoryData.reduce((acc, item) => {
    acc.totalOnHand += item.total_on_hand;
    acc.available += item.total_available;
    acc.expected += item.total_expected;
    acc.ordered += item.total_ordered;
    acc.onPurchase += item.on_purchase;
    acc.onOrder += item.on_order;
    acc.onBuild += item.on_build;
    acc.onTransfer += item.on_transfer;
    return acc;
  }, { totalOnHand: 0, available: 0, expected: 0, ordered: 0, onPurchase: 0, onOrder: 0, onBuild: 0, onTransfer: 0 });

  const chartData = {
    labels: ['Total On Hand', 'Available', 'Expected', 'Ordered', 'On Purchase', 'On Order', 'On Build', 'On Transfer'],
    datasets: [
      {
        label: 'Inventory Status',
        data: [totals.totalOnHand, totals.available, totals.expected, totals.ordered, totals.onPurchase, totals.onOrder, totals.onBuild, totals.onTransfer],
        backgroundColor: [
          '#4B77BE', // Total On Hand - Deep blue
          '#5CACEE', // Available - Light blue
          '#77B1A9', // Expected - Greenish blue
          '#72C6EF', // Ordered - Sky blue
          '#8E44AD', // On Purchase - Purple
          '#3498DB', // On Order - Blue
          '#2ECC71', // On Build - Green
          '#E74C3C'  // On Transfer - Red
        ],
        hoverBackgroundColor: [
          '#354A5F', // Total On Hand - Darker deep blue
          '#3E6B8E', // Available - Darker light blue
          '#5D8A76', // Expected - Darker greenish blue
          '#5DA0C3', // Ordered - Darker sky blue
          '#6C3483', // On Purchase - Darker purple
          '#2980B9', // On Order - Darker blue
          '#27AE60', // On Build - Darker green
          '#C0392B'  // On Transfer - Darker red
        ],
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        color: '#f8f8f8', // A light color close to white
        font: {
          size: 10, // Smaller font size
          family: 'Inter, sans-serif'
        },
        formatter: (value, ctx) => {
          return ctx.chart.data.labels[ctx.dataIndex];
        },
      },
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: 12,
            family: 'Inter, sans-serif',
            weight: '400',
            color: '#333'
          },
          boxWidth: 20,
          padding: 20,
        },
      },
      tooltip: {
        bodyFont: {
          size: 14,
          family: 'Inter, sans-serif',
          color: '#333'
        },
        titleFont: {
          size: 16,
          family: 'Inter, sans-serif',
          color: '#333'
        },
        backgroundColor: '#ffffff',
        borderColor: '#cccccc',
        borderWidth: 1,
        titleColor: '#333',
        bodyColor: '#333'
      }
    }
  };
  

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full max-w-lg h-custom-card-big flex flex-col justify-between">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faWarehouse} className="text-blue-600 mr-2" />
          Inventory Status
        </h2>
      </div>
      <div className="flex-grow relative">
        <Pie data={chartData} options={options} />
      </div>
      <hr className="my-4" />
      {/* Optionally, display totals for each status below the chart for clarity */}
    </div>
  );
};

export default InventoryStatusCard;
